import { useEffect, useRef, useLayoutEffect, useState } from "react";
import rough from "roughjs/bundled/rough.esm.js";

export default function RoughBorder({
  parentRef,
  radius = 10,
  padding = 3,
  color = "#18181b",
  strokeWidth = 1.2,
  roughness = 2,
  bowing = 2,
  className = "",
  style = {},
  shape = "rectangle", // "rectangle" or "circle"
}) {
  const svgRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient) return;
    if (!parentRef?.current) return;
    const el = parentRef.current;

    const updateSize = () => {
      setTimeout(() => {
        const rect = el.getBoundingClientRect();
        setSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });
      }, 50); // Sicherstellen, dass alles sichtbar ist
    };

    updateSize();

    // Bilder-Events (img)
    const imgs = el.querySelectorAll('img');
    imgs.forEach(img => img.addEventListener('load', updateSize));

    // ResizeObserver
    const ro = new window.ResizeObserver(updateSize);
    ro.observe(el);

    return () => {
      ro.disconnect();
      imgs.forEach(img => img.removeEventListener('load', updateSize));
    };
  }, [isClient, parentRef]);

  // Nachträgliches Forced-Update, falls Bilder/fonts nachladen
  useEffect(() => {
    if (!isClient) return;
    const t = setTimeout(() => {
      if (parentRef?.current) {
        const rect = parentRef.current.getBoundingClientRect();
        setSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [isClient, parentRef]);

  // Bei Window-Resize
  useEffect(() => {
    if (!isClient) return;
    const handler = () => {
      if (parentRef?.current) {
        const rect = parentRef.current.getBoundingClientRect();
        setSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        });
      }
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [isClient, parentRef]);

  useLayoutEffect(() => {
    if (!isClient) return;
    if (!svgRef.current || !size.width || !size.height) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);

    let node;
    if (shape === "circle") {
      // Draw a circle (use the smaller dimension as diameter)
      const diameter = Math.min(size.width, size.height) - strokeWidth - 2 * padding;
      const centerX = size.width / 2;
      const centerY = size.height / 2;
      const radiusCircle = diameter / 2;

      node = rc.circle(centerX, centerY, diameter, {
        roughness,
        bowing,
        stroke: color,
        strokeWidth,
        fill: "none",
      });
    } else {
      // Draw a rectangle
      node = rc.rectangle(
        padding + strokeWidth / 2,
        padding + strokeWidth / 2,
        size.width - strokeWidth - 2 * padding,
        size.height - strokeWidth - 2 * padding,
        {
          roughness,
          bowing,
          stroke: color,
          strokeWidth,
          fill: "none",
        }
      );
    }

    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness, bowing, padding, shape]);

  if (!isClient || size.width === 0 || size.height === 0) {
    return null;
  }

  return (
    <svg
      ref={svgRef}
      width={size.width}
      height={size.height}
      className={`absolute inset-0 w-full h-full pointer-events-none z-99 ${className}`}
      style={{ borderRadius: radius, ...style }}
    />
  );
}
